import {Fragment, ReactNode, useState} from "react";
import {css} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {KM_HM_TEST_IDS} from "@pg-mono/test-ids";
import {useUserDevice} from "@pg-mono/user-device";

import {zIndexes} from "@/features/km_app/constants/z_indexes";
import {HomeMatchAmplitudeLocation, HomeMatchPreferencesWizardAmplitudeButton} from "@/features/tracking/amplitude/home_match_amplitude_tracker";
import {UserFlowModal} from "@/features/user/components/modal/UserFlowModal";

import {HOME_MATCH_PREFERENCES_WIZARD_CONTENT} from "../../constants/preferences/home_match_preferences_wizard_content";
import {IHomeMatchPreferencesWizardMapActions} from "../../hooks/preferences/use_home_match_preferences_wizard_location_map";
import {useHomeMatchPreferencesWizardTracking} from "../../hooks/preferences/use_home_match_preferences_wizard_tracking";
import {HomeMatchPreferencesWizardStep} from "../../utils/get_home_match_preferences_wizard_steps";
import {HomeMatchPreferencesWizardExitLayer, HomeMatchPreferencesWizardExitSheet} from "./HomeMatchPreferencesWizardExit";

interface IProps {
    amplitudeLocation: HomeMatchAmplitudeLocation;
    canGoBack: boolean;
    children: ReactNode;
    /** Remounts the content when it changes. Defaults to the step. */
    contentKey?: string;
    description?: ReactNode;
    isExitSaveDisabled: boolean;
    isOpen: boolean;
    /** Parameter entry edits a single step: no progress bar and no exit prompt. */
    isParameterEntry: boolean;
    /** Phone opened on its own closes immediately. The exit prompt stays for a wizard that also asks the survey. */
    showExitPrompt?: boolean;
    isPrimaryDisabled: boolean;
    /** Set while the location map is open: it replaces the primary and back actions with apply and cancel. */
    mapActions?: IHomeMatchPreferencesWizardMapActions;
    onBack: () => void;
    onCloseWithoutSaving: () => void;
    onExitSave: () => void;
    onPrimaryClick: () => void;
    onRequestClose?: () => void;
    primaryLabel: string;
    progressStepIndex: number;
    step: HomeMatchPreferencesWizardStep;
    stepCount: number;
    title?: ReactNode;
    zIndex?: number;
}

export const HomeMatchPreferencesWizardModal = (props: IProps) => {
    const {
        amplitudeLocation,
        canGoBack,
        children,
        contentKey,
        description,
        isExitSaveDisabled,
        isOpen,
        isParameterEntry,
        isPrimaryDisabled,
        showExitPrompt = !isParameterEntry,
        mapActions,
        onBack,
        onCloseWithoutSaving,
        onExitSave,
        onPrimaryClick,
        onRequestClose,
        primaryLabel,
        progressStepIndex,
        step,
        stepCount,
        title,
        zIndex = zIndexes.listingModal
    } = props;
    const {isMobile} = useUserDevice();
    const [isExitOpen, setIsExitOpen] = useState(false);

    if (!isOpen && isExitOpen) setIsExitOpen(false);

    const trackClick = useHomeMatchPreferencesWizardTracking({amplitudeLocation, isExitOpen, isOpen, trackedStep: step});

    const progressValue = stepCount === 0 ? 0 : Math.round(((progressStepIndex + 1) / stepCount) * 100);

    const requestClose = () => {
        trackClick(HomeMatchPreferencesWizardAmplitudeButton.CLOSE);

        if (!showExitPrompt) return onCloseWithoutSaving();

        setIsExitOpen(true);
    };

    const closeExit = () => {
        setIsExitOpen(false);
    };

    const dismissExit = () => {
        trackClick(HomeMatchPreferencesWizardAmplitudeButton.CLOSE);
        onCloseWithoutSaving();
    };

    const saveAndClose = () => {
        trackClick(HomeMatchPreferencesWizardAmplitudeButton.CLOSE);
        onExitSave();
    };

    const goBack = () => {
        trackClick(HomeMatchPreferencesWizardAmplitudeButton.BACK);
        onBack();
    };

    const confirmStep = () => {
        trackClick(HomeMatchPreferencesWizardAmplitudeButton.SUBMIT);
        onPrimaryClick();
    };

    return (
        <Fragment>
            <UserFlowModal
                contentKey={contentKey ?? step}
                cover={
                    !isMobile && isExitOpen ? (
                        <HomeMatchPreferencesWizardExitLayer
                            isSaveDisabled={isExitSaveDisabled}
                            onDismiss={dismissExit}
                            onReturn={closeExit}
                            onSave={saveAndClose}
                        />
                    ) : undefined
                }
                dataTestId={KM_HM_TEST_IDS.PREFERENCES_WIZARD}
                description={description}
                footer={
                    mapActions ? (
                        <Button fullWidth onClick={mapActions.apply} size="small" textVariant="button_medium" type="button" variant="filled_secondary">
                            {HOME_MATCH_PREFERENCES_WIZARD_CONTENT.LOCATION_APPLY}
                        </Button>
                    ) : (
                        <Button
                            dataTestId={KM_HM_TEST_IDS.PREFERENCES_WIZARD_PRIMARY}
                            disabled={isPrimaryDisabled}
                            fullWidth
                            onClick={confirmStep}
                            size="small"
                            textVariant="button_medium"
                            type="button"
                            variant="filled_secondary"
                        >
                            {primaryLabel}
                        </Button>
                    )
                }
                isOpen={isOpen}
                onClose={onRequestClose ?? requestClose}
                progress={!isParameterEntry && progressStepIndex >= 0 ? progressValue : null}
                progressTestId={KM_HM_TEST_IDS.PREFERENCES_WIZARD_PROGRESS}
                secondaryAction={
                    mapActions ? (
                        <Button
                            className={backButtonStyle}
                            onClick={mapActions.cancel}
                            size="small"
                            textVariant="button_medium"
                            type="button"
                            variant="none_secondary"
                        >
                            {HOME_MATCH_PREFERENCES_WIZARD_CONTENT.LOCATION_CANCEL}
                        </Button>
                    ) : (
                        canGoBack && (
                            <Button
                                className={backButtonStyle}
                                dataTestId={KM_HM_TEST_IDS.PREFERENCES_WIZARD_BACK}
                                onClick={goBack}
                                size="small"
                                textVariant="button_medium"
                                type="button"
                                variant="none_secondary"
                            >
                                {HOME_MATCH_PREFERENCES_WIZARD_CONTENT.BACK}
                            </Button>
                        )
                    )
                }
                shouldCloseOnOverlayClick={isParameterEntry}
                settle={step === HomeMatchPreferencesWizardStep.SUMMARY}
                showIllustration={step !== HomeMatchPreferencesWizardStep.SUMMARY && !mapActions}
                title={title}
                zIndex={zIndex}
            >
                {children}
            </UserFlowModal>

            {isMobile && (
                <HomeMatchPreferencesWizardExitSheet
                    isOpen={isExitOpen}
                    isSaveDisabled={isExitSaveDisabled}
                    onDismiss={dismissExit}
                    onReturn={closeExit}
                    onSave={saveAndClose}
                />
            )}
        </Fragment>
    );
};

const backButtonStyle = css({
    borderWidth: 0,
    flexShrink: 0,
    height: "4.8rem",
    p: 0
});
import {Fragment, ReactNode, useState} from "react";
import {css} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {KM_HM_TEST_IDS} from "@pg-mono/test-ids";
import {useUserDevice} from "@pg-mono/user-device";

import {zIndexes} from "@/features/km_app/constants/z_indexes";
import {HomeMatchAmplitudeLocation, HomeMatchPreferencesWizardAmplitudeButton} from "@/features/tracking/amplitude/home_match_amplitude_tracker";
import {UserFlowModal} from "@/features/user/components/modal/UserFlowModal";

import {HOME_MATCH_PREFERENCES_WIZARD_CONTENT} from "../../constants/preferences/home_match_preferences_wizard_content";
import {IHomeMatchPreferencesWizardMapActions} from "../../hooks/preferences/use_home_match_preferences_wizard_location_map";
import {useHomeMatchPreferencesWizardTracking} from "../../hooks/preferences/use_home_match_preferences_wizard_tracking";
import {HomeMatchPreferencesWizardStep} from "../../utils/get_home_match_preferences_wizard_steps";
import {HomeMatchPreferencesWizardExitLayer, HomeMatchPreferencesWizardExitSheet} from "./HomeMatchPreferencesWizardExit";

interface IProps {
    amplitudeLocation: HomeMatchAmplitudeLocation;
    canGoBack: boolean;
    children: ReactNode;
    /** Remounts the content when it changes. Defaults to the step. */
    contentKey?: string;
    description?: ReactNode;
    isExitSaveDisabled: boolean;
    isOpen: boolean;
    /** Parameter entry edits a single step: no progress bar and no exit prompt. */
    isParameterEntry: boolean;
    /** Phone opened on its own closes immediately. The exit prompt stays for a wizard that also asks the survey. */
    showExitPrompt?: boolean;
    isPrimaryDisabled: boolean;
    /** Set while the location map is open: it replaces the primary and back actions with apply and cancel. */
    mapActions?: IHomeMatchPreferencesWizardMapActions;
    onBack: () => void;
    onCloseWithoutSaving: () => void;
    onExitSave: () => void;
    onPrimaryClick: () => void;
    onRequestClose?: () => void;
    primaryLabel: string;
    progressStepIndex: number;
    step: HomeMatchPreferencesWizardStep;
    stepCount: number;
    title?: ReactNode;
    zIndex?: number;
}

export const HomeMatchPreferencesWizardModal = (props: IProps) => {
    const {
        amplitudeLocation,
        canGoBack,
        children,
        contentKey,
        description,
        isExitSaveDisabled,
        isOpen,
        isParameterEntry,
        isPrimaryDisabled,
        showExitPrompt = !isParameterEntry,
        mapActions,
        onBack,
        onCloseWithoutSaving,
        onExitSave,
        onPrimaryClick,
        onRequestClose,
        primaryLabel,
        progressStepIndex,
        step,
        stepCount,
        title,
        zIndex = zIndexes.listingModal
    } = props;
    const {isMobile} = useUserDevice();
    const [isExitOpen, setIsExitOpen] = useState(false);

    if (!isOpen && isExitOpen) setIsExitOpen(false);

    const trackClick = useHomeMatchPreferencesWizardTracking({amplitudeLocation, isExitOpen, isOpen, trackedStep: step});

    const progressValue = stepCount === 0 ? 0 : Math.round(((progressStepIndex + 1) / stepCount) * 100);

    const requestClose = () => {
        trackClick(HomeMatchPreferencesWizardAmplitudeButton.CLOSE);

        if (!showExitPrompt) return onCloseWithoutSaving();

        setIsExitOpen(true);
    };

    const closeExit = () => {
        setIsExitOpen(false);
    };

    const dismissExit = () => {
        trackClick(HomeMatchPreferencesWizardAmplitudeButton.CLOSE);
        onCloseWithoutSaving();
    };

    const saveAndClose = () => {
        trackClick(HomeMatchPreferencesWizardAmplitudeButton.CLOSE);
        onExitSave();
    };

    const goBack = () => {
        trackClick(HomeMatchPreferencesWizardAmplitudeButton.BACK);
        onBack();
    };

    const confirmStep = () => {
        trackClick(HomeMatchPreferencesWizardAmplitudeButton.SUBMIT);
        onPrimaryClick();
    };

    return (
        <Fragment>
            <UserFlowModal
                contentKey={contentKey ?? step}
                cover={
                    !isMobile && isExitOpen ? (
                        <HomeMatchPreferencesWizardExitLayer
                            isSaveDisabled={isExitSaveDisabled}
                            onDismiss={dismissExit}
                            onReturn={closeExit}
                            onSave={saveAndClose}
                        />
                    ) : undefined
                }
                dataTestId={KM_HM_TEST_IDS.PREFERENCES_WIZARD}
                description={description}
                footer={
                    mapActions ? (
                        <Button fullWidth onClick={mapActions.apply} size="small" textVariant="button_medium" type="button" variant="filled_secondary">
                            {HOME_MATCH_PREFERENCES_WIZARD_CONTENT.LOCATION_APPLY}
                        </Button>
                    ) : (
                        <Button
                            dataTestId={KM_HM_TEST_IDS.PREFERENCES_WIZARD_PRIMARY}
                            disabled={isPrimaryDisabled}
                            fullWidth
                            onClick={confirmStep}
                            size="small"
                            textVariant="button_medium"
                            type="button"
                            variant="filled_secondary"
                        >
                            {primaryLabel}
                        </Button>
                    )
                }
                isOpen={isOpen}
                onClose={onRequestClose ?? requestClose}
                progress={!isParameterEntry && progressStepIndex >= 0 ? progressValue : null}
                progressTestId={KM_HM_TEST_IDS.PREFERENCES_WIZARD_PROGRESS}
                secondaryAction={
                    mapActions ? (
                        <Button
                            className={backButtonStyle}
                            onClick={mapActions.cancel}
                            size="small"
                            textVariant="button_medium"
                            type="button"
                            variant="none_secondary"
                        >
                            {HOME_MATCH_PREFERENCES_WIZARD_CONTENT.LOCATION_CANCEL}
                        </Button>
                    ) : (
                        canGoBack && (
                            <Button
                                className={backButtonStyle}
                                dataTestId={KM_HM_TEST_IDS.PREFERENCES_WIZARD_BACK}
                                onClick={goBack}
                                size="small"
                                textVariant="button_medium"
                                type="button"
                                variant="none_secondary"
                            >
                                {HOME_MATCH_PREFERENCES_WIZARD_CONTENT.BACK}
                            </Button>
                        )
                    )
                }
                shouldCloseOnOverlayClick={isParameterEntry}
                settle={step === HomeMatchPreferencesWizardStep.SUMMARY}
                showIllustration={step !== HomeMatchPreferencesWizardStep.SUMMARY && !mapActions}
                title={title}
                zIndex={zIndex}
            >
                {children}
            </UserFlowModal>

            {isMobile && (
                <HomeMatchPreferencesWizardExitSheet
                    isOpen={isExitOpen}
                    isSaveDisabled={isExitSaveDisabled}
                    onDismiss={dismissExit}
                    onReturn={closeExit}
                    onSave={saveAndClose}
                />
            )}
        </Fragment>
    );
};

const backButtonStyle = css({
    borderWidth: 0,
    flexShrink: 0,
    height: "4.8rem",
    p: 0
});
